import {useCallback, useDeferredValue, useEffect, useId, useMemo, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import clsx from "clsx";
import {ClipboardCopy, FileWarning, History, Lock, RefreshCw, Save, Trash2, Undo2, WifiOff, X} from "lucide-react";
import {mapApiError} from "../../../shared/api/errors";
import {DataState} from "../../../shared/components/DataState";
import {formatNumber} from "../../../shared/utils/format";
import {
    OFFLINE_COPY,
    OFFLINE_TITLE,
    clearStoredDraft,
    formatShortDateTime,
    queryWaitState,
    readStoredDraft,
    updatedLabel,
    writeStoredDraft,
} from "../appTextsContracts";
import {countryDocumentsKeys, loadCountryDocument, saveCountryDocument} from "../countryDocumentsApi";
import {
    DOCUMENT_CONFLICT_MESSAGE,
    DOCUMENT_FORBIDDEN_MESSAGE,
    EMPTY_DOCUMENT_MESSAGE,
    documentClientError,
    documentDraftStorageKey,
    documentLength,
    documentMaxLength,
    documentSaveMessage,
    documentsErrorMessage,
    isDocumentDirty,
    normalizeDocumentText,
    patchDocumentsList,
    storedDocumentDraft,
} from "../countryDocumentsContracts";
import {ConfirmDialog} from "./ConfirmDialog";
import {useTranslation} from "../../../shared/i18n";


const DRAFT_PERSIST_MS = 500;
// Result lines that the next edit may clear; errors and conflicts stay until handled.
const TRANSIENT_RESULTS = new Set(["success", "info"]);

const fieldErrorList = (fieldErrors = {}) => Object.entries(fieldErrors || {})
    .filter(([field]) => field !== "text")
    .flatMap(([, messages]) => messages);

export const CountryDocumentEditor = ({countryId, countryName, kind, label, canManage, tabId, panelId, onDirtyChange}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const documentKey = countryDocumentsKeys.document(countryId, kind);
    const storageKey = documentDraftStorageKey({countryId, kind});
    const counterId = useId();
    const documentQuery = useQuery({
        queryKey: documentKey,
        queryFn: () => loadCountryDocument({countryId, kind}),
    });
    const view = documentQuery.data;
    const savedText = typeof view?.text === "string" ? view.text : "";
    const canEdit = Boolean(view) && Boolean(canManage) && view.can_edit === true;
    const maxLength = documentMaxLength(view);

    // null = the editor shows the saved text; otherwise the typed text and the revision
    // it was started from (a background refetch never moves that revision).
    const [draft, setDraft] = useState(null);
    // The admin's own text kept aside after a conflict reload, to copy from.
    const [stash, setStash] = useState(null);
    const [result, setResult] = useState(null);
    const [saveFieldErrors, setSaveFieldErrors] = useState(null);
    const [reloading, setReloading] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [copyState, setCopyState] = useState(null);
    const [storedDraft, setStoredDraft] = useState(() => storedDocumentDraft(readStoredDraft(storageKey)));
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const stashRef = useRef(null);
    const persistRef = useRef({key: storageKey, draft: null, timer: null});

    const text = draft ? draft.text : savedText;
    const normalized = useMemo(() => normalizeDocumentText(text), [text]);
    const length = documentLength(normalized);
    const savedNormalized = useMemo(() => normalizeDocumentText(savedText), [savedText]);
    const dirty = canEdit && draft !== null && normalized !== savedNormalized;
    const editing = canEdit && draft !== null;
    const clientError = useMemo(() => (editing ? documentClientError(text, maxLength) : null), [editing, maxLength, text]);
    const previewText = useDeferredValue(normalized);
    const conflict = result?.kind === "conflict";

    const touch = useCallback(() => {
        setSaveFieldErrors(null);
        setResult((current) => (TRANSIENT_RESULTS.has(current?.kind) ? null : current));
    }, []);

    const forgetStoredDraft = useCallback(() => {
        persistRef.current.draft = null;
        clearStoredDraft(storageKey);
        setStoredDraft(null);
    }, [storageKey]);

    const saveMutation = useMutation({
        mutationFn: ({text: body, revision}) => saveCountryDocument({countryId, kind, text: body, revision}),
        onMutate: () => {
            setResult(null);
            setSaveFieldErrors(null);
        },
        onSuccess: (response) => {
            queryClient.setQueryData(documentKey, response);
            queryClient.setQueryData(countryDocumentsKeys.list(), (list) => patchDocumentsList(list, response));
            forgetStoredDraft();
            setDraft(null);
            setConfirm(null);
            setResult({kind: "success", message: documentSaveMessage(response)});
        },
        onError: (error) => {
            setConfirm(null);
            const status = error?.response?.status;
            if (status === 409) {
                setResult({kind: "conflict", message: DOCUMENT_CONFLICT_MESSAGE});
                return;
            }
            if (status === 403) {
                setResult({kind: "error", message: DOCUMENT_FORBIDDEN_MESSAGE});
                return;
            }
            if (status === 400 || status === 422) {
                const mapped = mapApiError(error);
                setSaveFieldErrors(Object.keys(mapped.fieldErrors).length ? mapped.fieldErrors : null);
                setResult({kind: "error", message: mapped.message});
                return;
            }
            setResult({kind: "error", message: documentsErrorMessage(error)});
        },
    });
    const saving = saveMutation.isPending;

    const updateText = (next) => {
        touch();
        setDraft((current) => (current ? {...current, text: next} : {text: next, baseRevision: view?.revision ?? null}));
    };

    const submit = () => {
        const current = draftRef.current;
        if (!current) return;
        // The revision the draft started from, not the latest one fetched: a text saved
        // by someone else since then must answer 409 instead of being overwritten.
        saveMutation.mutate({text: current.text, revision: current.baseRevision});
    };
    const save = () => {
        if (!dirty || clientError || conflict) return;
        if (!normalized && !view?.is_empty) {
            const name = view?.country?.name || countryName;
            setConfirm({
                title: t("sections.content.p183"),
                copy: t("sections.content.p184", {v0: label, v1: name ? t("sections.content.p185", {v0: name}) : ""}),
                confirmLabel: t("sections.content.p186"),
                onConfirm: submit,
            });
            return;
        }
        submit();
    };

    const discard = () => {
        setDraft(null);
        setSaveFieldErrors(null);
        setResult(null);
        forgetStoredDraft();
    };

    const reloadLatest = async () => {
        setReloading(true);
        const response = await documentQuery.refetch();
        setReloading(false);
        if (response.isError || !response.data) {
            setResult({
                kind: "conflict",
                message: t("sections.content.p68", {v0: documentsErrorMessage(response.error)}),
            });
            return;
        }
        queryClient.setQueryData(countryDocumentsKeys.list(), (list) => patchDocumentsList(list, response.data));
        const mine = draftRef.current?.text;
        setDraft(null);
        setSaveFieldErrors(null);
        setCopyState(null);
        if (typeof mine === "string" && isDocumentDirty(mine, response.data.text)) {
            // The typed text stays in this browser tab (sessionStorage) until it is saved
            // or hidden, and on screen next to the editor.
            setStash({text: mine});
            setResult({kind: "info", message: t("sections.content.p187")});
        } else {
            setResult({kind: "info", message: t("sections.content.p188")});
        }
    };

    const copyStash = async () => {
        if (!stash) return;
        try {
            await navigator.clipboard.writeText(stash.text);
            setCopyState("copied");
        } catch {
            stashRef.current?.focus();
            stashRef.current?.select();
            setCopyState("failed");
        }
    };
    const putStashIntoEditor = () => {
        const kept = stash;
        if (!kept) return;
        touch();
        setConfirm(null);
        setStash(null);
        setCopyState(null);
        setDraft({text: kept.text, baseRevision: view?.revision ?? null});
        setResult({kind: "info", message: t("sections.content.p189")});
    };
    const returnStash = () => {
        if (dirty) {
            setConfirm({
                title: t("sections.content.p190"),
                copy: t("sections.content.p191"),
                confirmLabel: t("sections.content.p192"),
                onConfirm: putStashIntoEditor,
            });
            return;
        }
        putStashIntoEditor();
    };
    const hideStash = () => {
        setStash(null);
        setCopyState(null);
        if (!dirty) forgetStoredDraft();
    };

    // Restore offer for a draft left in this browser tab (sidebar navigation, header
    // country switch and re-login cannot be blocked with BrowserRouter).
    const storedDiffers = Boolean(storedDraft) && Boolean(view) && isDocumentDirty(storedDraft.text, savedText);
    const storedOutdated = storedDiffers && storedDraft.baseRevision !== (view?.revision ?? null);
    const showRestore = canEdit && storedDiffers && draft === null && !stash;
    const restoreDraft = () => {
        const stored = storedDraft;
        setStoredDraft(null);
        if (!stored) return;
        touch();
        if (storedOutdated) {
            // Saved by someone else since: the editor keeps the current text and the draft
            // goes aside, the same as after a conflict.
            setStash({text: stored.text});
            setResult({kind: "info", message: t("sections.content.p193")});
            return;
        }
        setDraft({text: stored.text, baseRevision: stored.baseRevision});
    };

    // Throttled draft persistence; flushed on unmount so a remount does not lose the tail.
    useEffect(() => {
        const persist = persistRef.current;
        if (!dirty || !draft) {
            persist.draft = null;
            return;
        }
        persist.draft = {text: draft.text, baseRevision: draft.baseRevision};
        if (persist.timer) return;
        persist.timer = window.setTimeout(() => {
            persist.timer = null;
            if (persist.draft) writeStoredDraft(persist.key, persist.draft);
        }, DRAFT_PERSIST_MS);
    }, [dirty, draft]);
    useEffect(() => {
        const persist = persistRef.current;
        return () => {
            if (persist.timer) window.clearTimeout(persist.timer);
            persist.timer = null;
            if (persist.draft) writeStoredDraft(persist.key, persist.draft);
        };
    }, []);

    useEffect(() => {
        if (!dirty) return undefined;
        const warnBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", warnBeforeUnload);
        return () => window.removeEventListener("beforeunload", warnBeforeUnload);
    }, [dirty]);

    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

    const closeConfirm = useCallback(() => setConfirm(null), []);

    const panelProps = {
        "aria-labelledby": tabId,
        className: "content-editor content-documents-editor",
        id: panelId,
        role: "tabpanel",
    };

    const wait = queryWaitState(documentQuery);
    if (wait === "loading") {
        return (
            <section {...panelProps}>
                <div aria-label={t("sections.content.p194")} className="content-media-loading content-texts-loading">
                    <span /><span /><span />
                </div>
            </section>
        );
    }
    if (wait === "offline") {
        return (
            <section {...panelProps}>
                <div className="content-readonly-note"><WifiOff size={16} /><span>{OFFLINE_TITLE}. {OFFLINE_COPY}</span></div>
            </section>
        );
    }
    if (!view) {
        return (
            <section {...panelProps}>
                <div className="content-texts-state">
                    <DataState title={t("sections.content.p195")} copy={documentsErrorMessage(documentQuery.error)} />
                    <button className="admin-row-action" onClick={() => documentQuery.refetch()} type="button"><RefreshCw size={14} /> {t("sections.content.p70")}</button>
                </div>
            </section>
        );
    }

    const updated = updatedLabel(view);
    const textErrors = [
        ...(clientError ? [clientError] : []),
        ...(Array.isArray(saveFieldErrors?.text) ? saveFieldErrors.text : []),
    ];
    const otherErrors = fieldErrorList(saveFieldErrors);
    const overLimit = length > maxLength;
    const canSave = dirty && !saving && !reloading && !clientError && !conflict;
    const resultIsError = result && !TRANSIENT_RESULTS.has(result.kind);
    const counter = dirty ? t("sections.content.p33") : t("sections.content.p74");

    return (
        <section {...panelProps}>
            <div className="content-workspace-head">
                <div>
                    <h3>{view.title || label}</h3>
                    <p>{t("sections.content.p196")} {view.country?.name || countryName}{t("sections.content.p197")}</p>
                    {updated ? (
                        <div className="content-texts-meta"><span><History aria-hidden="true" size={13} /> {updated}</span></div>
                    ) : null}
                </div>
            </div>

            {!canEdit ? (
                <div className="content-readonly-note"><Lock size={16} /><span>{t("sections.content.p198")}</span></div>
            ) : null}

            {view.is_empty && !dirty ? (
                <div className="content-readonly-note" role="status"><FileWarning size={16} /><span>{EMPTY_DOCUMENT_MESSAGE}</span></div>
            ) : null}

            {showRestore ? (
                <div className="content-texts-draft-banner" role="status">
                    <span>
                        {t("sections.content.p80")}{storedDraft.savedAt ? t("sections.content.p81", {v0: formatShortDateTime(storedDraft.savedAt)}) : ""}.
                        {storedOutdated ? t("sections.content.p199") : ""}
                    </span>
                    <div className="content-row-actions">
                        <button className="admin-row-action" onClick={restoreDraft} type="button"><Undo2 size={14} /> {t("sections.content.p83")}</button>
                        <button className="admin-row-action" onClick={forgetStoredDraft} type="button"><Trash2 size={14} /> {t("sections.content.p84")}</button>
                    </div>
                </div>
            ) : null}

            <div className="content-documents-layout">
                <div className="content-documents-input">
                    <label className="admin-field content-documents-field">
                        <span>{t("sections.content.p200")}</span>
                        <textarea
                            aria-describedby={counterId}
                            aria-invalid={textErrors.length ? "true" : undefined}
                            aria-label={t("sections.content.p201", {v0: label})}
                            className="admin-textarea content-documents-textarea"
                            onChange={(event) => updateText(event.target.value)}
                            placeholder={canEdit ? t("sections.content.p202") : ""}
                            readOnly={!canEdit}
                            value={text}
                        />
                    </label>
                    <span className={clsx("content-documents-counter", overLimit && "is-error")} id={counterId}>
                        {formatNumber(length)} / {formatNumber(maxLength)} {t("sections.content.p203")}
                    </span>
                    {textErrors.map((message) => <p className="content-field-error" key={message}>{message}</p>)}
                </div>
                <section aria-label={t("sections.content.p204")} className="content-documents-preview">
                    <h4>{t("sections.content.p204")}</h4>
                    <div className="content-documents-phone">
                        {previewText ? (
                            <article aria-label={t("sections.content.p205")} className="content-documents-preview-text">{previewText}</article>
                        ) : (
                            <p className="content-documents-preview-empty">{EMPTY_DOCUMENT_MESSAGE}</p>
                        )}
                    </div>
                </section>
            </div>

            {stash ? (
                <section aria-label={t("sections.content.p206")} className="content-documents-stash">
                    <div className="content-documents-stash-head">
                        <div>
                            <strong>{t("sections.content.p206")}</strong>
                            <span>{t("sections.content.p207")}</span>
                        </div>
                        <div className="content-row-actions">
                            <button className="admin-row-action" onClick={copyStash} type="button">
                                <ClipboardCopy size={14} /> {copyState === "copied" ? t("sections.content.p208") : t("sections.content.p209")}
                            </button>
                            {canEdit ? (
                                <button className="admin-row-action" onClick={returnStash} type="button"><Undo2 size={14} /> {t("sections.content.p210")}</button>
                            ) : null}
                            <button className="admin-row-action" onClick={hideStash} type="button"><X size={14} /> {t("sections.content.p211")}</button>
                        </div>
                    </div>
                    {copyState === "failed" ? (
                        <p className="content-field-error">{t("sections.content.p212")}</p>
                    ) : null}
                    <textarea
                        aria-label={t("sections.content.p206")}
                        className="admin-textarea content-documents-stash-text"
                        readOnly
                        ref={stashRef}
                        value={stash.text}
                    />
                </section>
            ) : null}

            {canEdit ? (
                <div className="content-texts-actionbar">
                    {result ? (
                        <div className={resultIsError ? "content-result content-texts-result is-error" : "content-result content-texts-result"} role={resultIsError ? "alert" : "status"}>
                            <span>
                                {result.message}
                                {otherErrors.length ? ` ${otherErrors.join(" ")}` : ""}
                            </span>
                            {conflict ? (
                                <button className="admin-row-action" disabled={reloading} onClick={reloadLatest} type="button">
                                    <RefreshCw size={14} /> {reloading ? t("sections.content.p105") : t("sections.content.p213")}
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="content-texts-actionbar-row">
                        <span className="content-texts-counter">{counter}</span>
                        <div className="content-editor-actions">
                            <button className="admin-button admin-button-secondary" disabled={!draft || saving} onClick={discard} type="button"><Undo2 size={15} /> {t("sections.content.p108")}</button>
                            <button className="admin-button admin-button-primary" disabled={!canSave} onClick={save} type="button"><Save size={15} /> {saving ? t("sections.content.p109") : t("sections.content.p110")}</button>
                        </div>
                    </div>
                </div>
            ) : null}

            <ConfirmDialog
                cancelLabel={t("sections.content.p180")}
                confirmLabel={confirm?.confirmLabel}
                copy={confirm?.copy}
                onCancel={closeConfirm}
                onConfirm={() => confirm?.onConfirm?.()}
                open={Boolean(confirm)}
                pending={saving}
                title={confirm?.title}
            />
        </section>
    );
};
